<script setup lang="ts">
import { computed, reactive, ref, toRefs, watch } from "vue";
import { useI18n } from "vue-i18n";
import useProfileDownload from "../../../utils/buttons-logic/profileDownloadLogic";
import useProfileView from "../../../utils/buttons-logic/profileViewLogic";
import useQuestionnaireSend from "../../../utils/buttons-logic/sendQuestionnaireLogic";
import useGenerateProfile from "../../../utils/buttons-logic/generateProfileLogic";
import useDeleteParticipant from "../../../utils/buttons-logic/deleteParticipantButtonLogic";
import useShareProfile from "../../../utils/buttons-logic/profileShareLogic";
import {
    Participant,
    ParticipantActivityType,
    ParticipantStatus,
    ProductLanguages,
    Project,
    Trainer
} from "../../../types/PersologOnlineTypes";
import { useConfirm } from "primevue/useconfirm";
import { isMobileView } from "../../../utils/GeneralUtils.ts";
import { ProfileAdministrationAPI } from "../../../api";
import { useFlag } from "@unleash/proxy-client-vue";
import DirectInputDialog from "./DirectInputDialog.vue";
import { useMoveRoleCheck } from "../../../composables/useMoveRoleCheck.ts";

const { t } = useI18n();
const confirm = useConfirm();
// Unleash gates the rollout on dev/staging/production. In environments that
// run without a flag provider (VITE_APP_USE_UNLEASH=false: localhost and the
// ci e2e stack) useFlag always reports false, which would hide the feature
// entirely — treat it as enabled there, matching the dev flag state.
const directInputFrontendEnabled =
    import.meta.env.VITE_APP_USE_UNLEASH === "true" ? useFlag("direct_input_frontend_enabled") : ref(true);

const props = defineProps({
    participant: {
        type: Object as () => Participant,
        required: true,
    },
    project: {
        type: Object as () => Project,
        required: true,
    },
    trainer: {
        type: Object as () => Trainer,
        required: true,
    },
    profileQuantity: {
        type: Number,
        required: true,
    },
    productLanguages: {
        type: Object as () => ProductLanguages | null,
        required: true,
    },
    isDirectLink: {
        type: Boolean,
        required: true,
    },
    withLabel: {
        type: Boolean,
        default: false,
    },
    backgroundColor: {
        type: String,
        default: "bg-persolog-blue",
    },
});

const emit = defineEmits([
    "update",
    "update:participant",
    "share:to:participant",
    "delete:participant",
    "reload:participants",
    "update:collapsed",
    "update:project",
]);

const profileQuantity = ref(props.profileQuantity);
const actionLoading = computed(
    () =>
        viewLoading.value ||
        downloadLoading.value ||
        shareProfileLoading.value ||
        questionnaireSendLoading.value ||
        participantDeletionLoading.value ||
        profileGenerationLoading.value ||
        moveLoading.value,
);

/**
 * Logic for view profile
 */
const { participant, project, productLanguages } = toRefs(props);
const {
    isDisabled: viewDisabled,
    items: viewItems,
    loading: viewLoading,
    fetchLanguages: updateLanguagesForViewProfile,
} = useProfileView(participant, project);

/**
 * Logic for move participant
 */
const moveInfoVisible = ref(false);
const moveDialogVisible = ref(false);
const directInputDialogVisible = ref(false);
const compatibleProjects = ref<Project[]>([]);
const selectedTargetProject = ref<Project | null>(null);
const moveLoading = ref(false);
const moveError = ref<string | null>(null);
const { droppedRole: moveDroppedRole } = useMoveRoleCheck(participant, project, selectedTargetProject);

async function openMoveDialog() {
    if (!props.participant.id) return
    moveDialogVisible.value = true;
    selectedTargetProject.value = null;
    compatibleProjects.value = [];
    moveLoading.value = true;

    try {
        const projects = await ProfileAdministrationAPI.fetchCompatibleProjects(
            props.trainer.id,
            props.participant.id,
        );
        compatibleProjects.value = projects.filter((p) => p.id !== props.project.id);
    } finally {
        moveLoading.value = false;
    }
}

async function confirmMove() {
    if (!selectedTargetProject.value?.id || !props.participant.id) return;

    moveLoading.value = true;
    moveError.value = null;

    try {
        await ProfileAdministrationAPI.moveParticipant(
            selectedTargetProject.value.id,
            props.participant.id,
        );

        await ProfileAdministrationAPI.logParticipantActivity(
            props.participant.id,
            ParticipantActivityType.PARTICIPANT_MOVED
        );

        moveDialogVisible.value = false;
        emit("reload:participants");
        emit("update:project", selectedTargetProject.value);

    } catch (error: unknown) {
        console.error("Error moving participant:", error);
        moveError.value = t('project-details.participants.move.description.error');
    } finally {
        moveLoading.value = false;
    }
}

/**
 * Logic for download profile
 */
const {
    isDisabled: downloadDisabled,
    items: downloadItems,
    loading: downloadLoading,
    fetchLanguages: updateLanguagesForDownloadProfile,
} = useProfileDownload(participant, project);

const {
    items: generateItems,
    isDisabled: generateDisabled,
    loading: profileGenerationLoading,
} = useGenerateProfile(project, participant, productLanguages);

const { activateOrRemind, loading: questionnaireSendLoading } = useQuestionnaireSend(
    participant,
    project,
    props.trainer,
);

const {
    affectedGroupCharts,
    deleteParticipant,
    deletionMessage,
    loadAffectedGroupCharts,
    loading: participantDeletionLoading,
} = useDeleteParticipant(
    props.isDirectLink,
    computed(() => props.participant),
);

// The confirmation dialog is shared with sharing a profile: only the deletion lists the group charts.
const confirmingDeletion = ref(false);

const { shareProfile, loading: shareProfileLoading } = useShareProfile(
    props.participant,
    props.project,
    props.trainer,
);

const actions = reactive([
    {
        id: "1",
        label: t("project-details.participants.actionsLabels.view-profile"),
        icon: "pi pi-eye",
        items: viewItems,
        command: () => {
            handleAction("viewProfile");
        },
        disabled: computed(() => viewDisabled.value),
    },
    {
        id: "2",
        label: t("project-details.participants.actionsLabels.download-profile"),
        icon: "pi pi-download",
        items: downloadItems,
        command: () => {
            handleAction("downloadProfile");
        },
        disabled: computed(() => downloadDisabled.value),
    },
    {
        id: "3",
        label:
            props.participant.status === ParticipantStatus.ACTIVATED || props.participant.status === ParticipantStatus.STARTED
                ? t("project-details.participants.callForAction.remind")
                : t("project-details.participants.callForAction.activate"),
        icon: "pi pi-send",
        command: () => handleAction("sendQuestionnaire"),
        disabled: computed(
            () =>
                !(
                    (props.participant?.status === ParticipantStatus.CREATED && profileQuantity.value > 0) ||
                    props.participant?.status === ParticipantStatus.ACTIVATED ||
                    props.participant?.status === ParticipantStatus.STARTED
                ),
        ),
    },
    {
        id: "4",
        label: computed(() =>
            props.project.recipients === "TRAINER"
                ? t("project-details.participants.actionsLabels.share-profile")
                : t("project-details.participants.actionsLabels.resend-profile"),
        ),
        icon: "pi pi-file-export",
        command: () => handleAction("shareProfile"),
        disabled: computed(
            () => !["GENERATED", "NOTIFIED"].includes(props.participant.status!) || props.project.anonymous,
        ),
    },
    {
        id: "5",
        label: t("project-details.participants.actionsLabels.generate-profile"),
        icon: "pi pi-language",
        items: generateItems,
        command: () => {},
        disabled: computed(() => generateDisabled.value),
    },
    {
        id: "6",
        label: t("project-details.participants.actionsLabels.more-info"),
        icon: "pi pi-info-circle",
        command: () => handleAction("showInfo"),
        disabled: false,
    },
    {
        id: "7",
        label: t("project-details.participants.actionsLabels.move-participant"),
        icon: "pi pi-arrow-right-arrow-left",
        command: () => handleAction("moveParticipant"),
        disabled: computed(() =>
            props.isDirectLink || props.project.anonymous || props.participant.status === ParticipantStatus.DELETED
        ),
    },
    {
        id: "8",
        label: t("project-details.participants.actionsLabels.direct-input"),
        icon: "pi pi-pencil",
        command: () => handleAction("openDirectInput"),
        disabled: computed(
            () => ![ParticipantStatus.CREATED, ParticipantStatus.ACTIVATED, ParticipantStatus.STARTED].includes(props.participant.status!)
        ),
    },
    {
        separator: true,
        id: "separator-2",
    },
    {
        separator: true,
        disabled: true,
        id: "separator-1",
    },
    {
        id: "0",
        label: computed(() =>
            props.participant.status === ParticipantStatus.DELETED && !props.isDirectLink
                ? t("project-details.participants.actionsLabels.delete-participant-permanently")
                : t("project-details.participants.actionsLabels.delete-participant"),
        ),
        icon: "pi pi-trash",
        command: () => handleAction("deleteParticipant"),
        disabled: computed(
            () => props.isDirectLink && props.participant.status === ParticipantStatus.DELETED,
        ),
    },
]);

/**
 * Sort the actions by disabled status and id. Ignore the separator and delete participant actions.
 */
const sortedActions = computed(() => {
    // Assuming the last two actions are 'separator' and 'delete participant'
    const mainActions = actions.slice(0, -2);
    const separator = actions[actions.length - 2];
    const deleteAction = actions[actions.length - 1];

    const sortedMainActions = mainActions
        .sort((a, b) => {
            // Separate disabled and non-disabled, non-disabled first
            if (a.disabled !== b.disabled) {
                return a.disabled ? 1 : -1;
            }
            // Within each group, sort by id
            return a.id.localeCompare(b.id);
        })
        .filter((action) => (isMobileView() && action.id !== "1") || !isMobileView())
        .filter((action) => action.id !== "8" || directInputFrontendEnabled.value)
        // Anonymous participants (pseudonym, usually no address) never receive their profile.
        .filter((action) => action.id !== "4" || !props.participant.anonymous);

    // Recombine the sorted actions with the separator and delete action at the end. Deleted direct link
    // participants keep their entry (pseudo-delete), so the delete action is not offered for them.
    return [
        ...sortedMainActions,
        ...(props.participant.status === ParticipantStatus.DELETED && props.isDirectLink
            ? []
            : [separator, deleteAction]),
    ];
});

/**
 * Action handlers for the different actions.
 */
const actionHandlers: { [key: string]: () => void | Promise<void> } = {
    viewProfile: () => {
        updateLanguagesForViewProfile();
    },
    downloadProfile: () => {
        updateLanguagesForDownloadProfile();
    },
    sendQuestionnaire: async () => {
        const result = await activateOrRemind();
        if (result !== null) {
            emit("update:participant", [result]);
        }
        emit("reload:participants");
    },
    shareProfile: () => {
        shareConfirm();
    },
    deleteParticipant: () => {
        deletionConfirm();
    },
    showInfo: () => {
        emit("update:collapsed", false);
    },
    moveParticipant: () => openMoveDialog(),
    openDirectInput: () => { directInputDialogVisible.value = true; },
    mainAction: () => {
        // Collapse the options menu
    },
};

/**
 * Handle the action by calling the corresponding action handler.
 * @param action The action to handle.
 */
const handleAction = (action: string) => {
    if (actionHandlers[action]) {
        actionHandlers[action]();
    } else {
        console.log(`Unknown action: ${action}`);
    }
};

/**
 * Show a confirmation dialog before deleting a participant, naming the group charts whose PDFs go with them.
 */
const deletionConfirm = async () => {
    await loadAffectedGroupCharts();
    confirmingDeletion.value = true;
    confirm.require({
        group: "participantActionConfirmationDialog" + props.participant.id,
        message: deletionMessage.value.message,
        header: deletionMessage.value.header,
        acceptLabel: t("buttons.delete"),
        accept: async () => {
            await deleteParticipant();
            emit("delete:participant", props.participant);
        },
    });
};

/**
 * Show a confirmation dialog before sharing a profile.
 */
const shareConfirm = () => {
    confirmingDeletion.value = false;
    confirm.require({
        group: "participantActionConfirmationDialog" + props.participant.id,
        message:
            props.participant?.firstName || props.participant?.name
                ? t("project-details.participants.share.message.single", {
                      displayName: `${props.participant?.firstName} ${props.participant?.name}`,
                  })
                : t("project-details.participants.share.message.single", {
                      displayName: props.participant?.email,
                  }),
        header: t("project-details.participants.share.heading"),
        acceptLabel: t("buttons.share"),
        accept: () => {
            shareProfile();
            emit("share:to:participant", props.participant);
        },
    });
};

watch(
    () => props.profileQuantity,
    (newProfileQuantity) => {
        // Update profileQuantity when props.profileQuantity changes
        profileQuantity.value = newProfileQuantity;
    },
    { immediate: true },
);

const menu = ref();
const toggle = (event: unknown) => {
    menu.value.toggle(event);
};
</script>

<template>
    <div :class="{ 'w-full': withLabel }">
        <div class="card flex justify-center" :class="{ 'w-full': withLabel }">
            <Button
                :id="`participant-actions-button_btn_more-${project.id}-${participant.id}`"
                type="button"
                :icon="actionLoading ? 'pi pi-spin pi-spinner' : withLabel ? '' : 'pi pi-ellipsis-h'"
                icon-pos="right"
                :disabled="actionLoading"
                :label="withLabel ? t('project-details.participants.actionsLabels.more-actions') : ''"
                @click="toggle"
                aria-haspopup="true"
                aria-controls="overlay_tmenu"
                :class="[backgroundColor, { 'w-full': withLabel }]"
            />
            <TieredMenu ref="menu" id="overlay_tmenu" :model="sortedActions" popup />
        </div>
        <ConfirmDialog
            :group="'participantActionConfirmationDialog' + props.participant.id"
            :dismissable-mask="true"
            :block-scroll="true"
            :draggable="false"
            style="max-width: min(40rem, 95vw)"
            id="participantActionConfirmationDialog"
        >
            <template #container="{ message: dialogMessage, acceptCallback, rejectCallback }">
                <div class="flex flex-col items-center p-8 bg-surface rounded">
                    <div
                        class="rounded-full bg-brand text-brand-contrast inline-flex justify-center items-center h-24 w-24 -mt-20"
                    >
                        <i class="pi pi-question text-5xl text-white"></i>
                    </div>
                    <span class="font-bold text-2xl block mb-2 mt-6">{{ dialogMessage.header }}</span>
                    <div class="flex flex-col gap-4">
                        <p class="mb-0">{{ dialogMessage.message }}</p>
                        <div
                            v-if="confirmingDeletion && affectedGroupCharts.length > 0"
                            :id="`participant-action-button_group-charts-${project.id}-${participant.id}`"
                        >
                            <p class="m-0">{{ t("project-details.participants.delete.group-charts.intro") }}</p>
                            <ul class="list-disc pl-6 my-2">
                                <li v-for="chart in affectedGroupCharts" :key="chart"><strong>{{ chart }}</strong></li>
                            </ul>
                            <p class="m-0">{{ t("project-details.participants.delete.group-charts.consequence") }}</p>
                        </div>
                    </div>
                    <div class="flex items-center gap-2 mt-6">
                        <Button
                            :id="`participant-action-button_btn_confirm-${project.id}-${participant.id}`"
                            :label="dialogMessage.acceptLabel"
                            @click="acceptCallback"
                        ></Button>
                        <Button
                            :id="`participant-action-button_btn_reject-${project.id}-${participant.id}`"
                            :label="t('buttons.cancel')"
                            outlined
                            @click="rejectCallback"
                        ></Button>
                    </div>
                </div>
            </template>
        </ConfirmDialog>
        <Dialog
            v-model:visible="moveDialogVisible"
            modal
            :draggable="false"
            :dismissable-mask="true"
            :header="t('project-details.participants.actionsLabels.move-participant')"
            style="width: min(40rem, 95vw)"
        >
            <div class="flex flex-col gap-4">
                <div
                    v-if="moveLoading && compatibleProjects.length === 0"
                    class="mt-2 flex items-center gap-2 text-sm text-content-muted"
                >
                    <i class="pi pi-spin pi-spinner"></i>
                    <span>{{ t("project-details.participants.move.description.searching") }}</span>
                </div>
                <div v-if="compatibleProjects.length === 0 && !moveLoading" class="mt-2 text-sm text-content-muted">
                    {{ t("project-details.participants.move.description.failed") }}
                </div>

                <div v-if="compatibleProjects.length > 0" class="mt-2 text-sm text-content-muted">
                    {{ t("project-details.participants.move.description.success") }}
                </div>
                <Message v-if="moveError" severity="error" class="mb-4">
                    {{ moveError }}
                </Message>

                <Select
                    v-if="compatibleProjects.length > 0"
                    :id="`participant-actions-button_select_${participant.id}`"
                    v-model="selectedTargetProject"
                    :options="compatibleProjects"
                    option-label="name"
                    :placeholder="t('accessibility.checkboxes.selectOption')"
                    :loading="moveLoading"
                    class="w-full"
                />
                <Message
                    v-if="moveDroppedRole"
                    :id="`participant-actions-button_move-role-warning_${participant.id}`"
                    severity="warn"
                >
                    {{ t("project-details.participants.move.description.roleUnavailable", { role: moveDroppedRole }) }}
                </Message>

                <Fieldset :collapsed="!moveInfoVisible" @update:collapsed="(v) => (moveInfoVisible = !v)" :legend="t('project-details.participants.move.info.heading')" :toggleable="true">
                    <template #toggleicon>
                        <i v-if="moveInfoVisible" class="pi pi-times-circle"></i>
                        <i v-else class="pi pi-question-circle"></i>
                    </template>
                    <div class="text-sm">
                    <p class="mb-4">{{ t('project-details.participants.move.info.intro') }}</p>
                        <ul>
                            <li class="mb-0">
                                <strong>{{ t('project-details.participants.move.info.directLinkHeading') }}</strong><br>
                                {{ t('project-details.participants.move.info.directLinkText') }}
                            </li>
                            <li class="mb-0">
                                <strong>{{ t('project-details.participants.move.info.productHeading') }}</strong><br>
                                {{ t('project-details.participants.move.info.productText') }}
                            </li>
                            <li class="mb-0">
                                <strong>{{ t('project-details.participants.move.info.recipientsHeading') }}</strong><br>
                                {{ t('project-details.participants.move.info.recipientsText') }}
                            </li>
                            <li class="mb-0">
                                <strong>{{ t('project-details.participants.move.info.additionalRecipientHeading') }}</strong><br>
                                {{ t('project-details.participants.move.info.additionalRecipientText') }}
                            </li>
                            <li class="mb-0">
                                <strong>{{ t('project-details.participants.move.info.emailHeading') }}</strong><br>
                                {{ t('project-details.participants.move.info.emailText') }}
                            </li>
                            <li class="mb-0">
                                <strong>{{ t('project-details.participants.move.info.departmentRoleHeading') }}</strong><br>
                                {{ t('project-details.participants.move.info.departmentRoleText') }}
                            </li>
                        </ul>
                    </div>
                </Fieldset>

                <div class="flex justify-end gap-2 mt-2">
                    <Button
                        outlined
                        :label="t('buttons.cancel')"
                        @click="moveDialogVisible = false"
                        :disabled="moveLoading"
                    />
                    <Button
                        :id="`participant-actions-button_move-button_${participant.id}`"
                        v-if="compatibleProjects.length > 0"
                        :label="t('buttons.move')"
                        icon="pi pi-arrow-right"
                        @click="confirmMove"
                        :disabled="!selectedTargetProject || moveLoading"
                        :loading="moveLoading"
                    />
                </div>
            </div>
        </Dialog>
        <DirectInputDialog
            :visible="directInputDialogVisible"
            :participant="participant"
            :project="project"
            :trainer="trainer"
            @update:visible="directInputDialogVisible = $event"
            @saved="$emit('reload:participants')"
        />
    </div>
</template>
